/**
 * Role-Tuning-Dashboard.
 *
 * Zeigt aggregierte RBAC-Verstöße aus dem Shadow-Log pro (Rolle, Ressource,
 * Level). Admins können fehlende Rechte mit einem Klick in die Rolle nachziehen,
 * bevor RBAC_ENFORCE auf true geflippt wird.
 */
import React, { useEffect, useState, useCallback } from 'react';
import { RefreshCw, Loader2, Plus, Sliders, Clock, Users as UsersIcon } from 'lucide-react';
import api from '@/lib/axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';

const LEVEL_COLOR = {
  read: 'bg-sky-100 text-sky-700',
  write: 'bg-amber-100 text-amber-700',
  delete: 'bg-rose-100 text-rose-700',
};

function formatRelative(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  const diffMs = Date.now() - d.getTime();
  const diffMin = Math.round(diffMs / 60000);
  if (diffMin < 1) return 'gerade eben';
  if (diffMin < 60) return `vor ${diffMin} Min`;
  const diffH = Math.round(diffMin / 60);
  if (diffH < 24) return `vor ${diffH} Std`;
  const diffD = Math.round(diffH / 24);
  return `vor ${diffD} T`;
}

export default function RoleTuningTab({ roles, resources, reload }) {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [applying, setApplying] = useState(null);  // row key while applying

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get('/admin/rbac/shadow-log/stats');
      setStats(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Laden fehlgeschlagen');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const clearBuffer = async () => {
    if (!window.confirm('Shadow-Log wirklich leeren? Die Events gehen dauerhaft verloren.')) return;
    try {
      await api.post('/admin/rbac/shadow-log/clear');
      toast.success('Shadow-Log geleert');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Leeren fehlgeschlagen');
    }
  };

  const applyPermission = async (item) => {
    const role = roles.find((r) => r.id === item.role_id);
    if (!role) return toast.error(`Rolle "${item.role}" nicht gefunden`);
    const confirmMsg = `Rolle "${item.role}" um "${item.resource}: ${item.required}" erweitern?\n\n`
      + `${item.count} Verstöße von ${item.user_count} User:in(nen) aufgezeichnet.`;
    if (!window.confirm(confirmMsg)) return;

    // Build the full permissions array from current role + new one
    const key = `${item.role_id}:${item.resource}:${item.required}`;
    setApplying(key);
    try {
      // Start from the role's existing permissions
      const permMap = {};
      resources.forEach((r) => { permMap[r.key] = 'none'; });
      (role.permissions || []).forEach((p) => { permMap[p.resource] = p.level; });
      // Only upgrade, never downgrade
      const LEVEL_ORDER = { none: 0, read: 1, write: 2, delete: 3 };
      if (LEVEL_ORDER[permMap[item.resource]] < LEVEL_ORDER[item.required]) {
        permMap[item.resource] = item.required;
      }
      await api.put(`/admin/rbac/roles/${role.id}/permissions`, {
        permissions: resources.map((r) => ({ resource: r.key, level: permMap[r.key] })),
      });
      toast.success(`Rolle "${item.role}" aktualisiert: ${item.resource} → ${item.required}`);
      await Promise.all([reload(), load()]);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Speichern fehlgeschlagen');
    } finally {
      setApplying(null);
    }
  };

  if (loading && !stats) {
    return <div className="text-slate-400 p-8"><Loader2 className="w-5 h-5 animate-spin inline mr-2" /> Shadow-Log wird geladen…</div>;
  }

  const items = stats?.items || [];
  const enforceEnabled = stats?.enforce_enabled;

  return (
    <div className="space-y-4" data-testid="rbac-role-tuning-tab">
      {/* Header card */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 bg-gradient-to-br from-brand-red/5 to-slate-50 border border-brand-red/20 rounded-xl p-5">
        <div className="flex items-start gap-3">
          <Sliders className="w-6 h-6 text-brand-red mt-1" />
          <div>
            <h2 className="text-lg font-semibold text-slate-900">Rechte-Tuning (Shadow-Analyse)</h2>
            <p className="text-sm text-slate-600 mt-1 max-w-2xl">
              Aggregiert fehlende Rechte pro Rolle. Mit einem Klick lässt sich das
              Recht nachziehen, bevor du <code className="bg-white px-1 py-0.5 rounded border border-slate-200 text-xs">RBAC_ENFORCE=true</code> schaltest.
              Aktueller Modus: {enforceEnabled
                ? <Badge className="bg-rose-600 text-white ml-1">Hart erzwungen</Badge>
                : <Badge className="bg-emerald-600 text-white ml-1">Shadow (nur Log)</Badge>}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={load} data-testid="rbac-tuning-reload">
            <RefreshCw className={`w-4 h-4 mr-1 ${loading ? 'animate-spin' : ''}`} /> Neu laden
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={clearBuffer}
            className="text-rose-600 hover:text-rose-700"
            data-testid="rbac-tuning-clear"
          >
            Log leeren
          </Button>
        </div>
      </div>

      {/* KPI cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        <KpiCard label="Verstöße gesamt" value={stats?.total_events ?? 0} />
        <KpiCard label="Eindeutige Lücken" value={stats?.unique_buckets ?? 0} />
        <KpiCard
          label="Betroffene Rollen"
          value={new Set(items.map((i) => i.role)).size}
        />
      </div>

      {/* Empty state */}
      {items.length === 0 ? (
        <div className="bg-white border border-dashed border-slate-300 rounded-xl p-10 text-center">
          <p className="text-slate-500">
            Keine Shadow-Events im aktuellen Buffer. Das heißt entweder, dass alle
            aktiven Rollen die nötigen Rechte haben — oder dass noch keine neuen
            Rollen-User Endpoints aufgerufen haben.
          </p>
        </div>
      ) : (
        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
          <table className="w-full text-sm" data-testid="rbac-tuning-table">
            <thead className="bg-slate-50 text-xs uppercase text-slate-500">
              <tr>
                <th className="text-left px-4 py-3">Rolle</th>
                <th className="text-left px-4 py-3">Ressource</th>
                <th className="text-left px-4 py-3">Benötigt</th>
                <th className="text-right px-4 py-3">Verstöße</th>
                <th className="text-left px-4 py-3">Betroffene User:innen</th>
                <th className="text-left px-4 py-3">Zuletzt</th>
                <th className="text-right px-4 py-3">Aktion</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {items.map((item, idx) => {
                const key = `${item.role_id}:${item.resource}:${item.required}`;
                const isApplying = applying === key;
                const roleExists = !!item.role_id;
                return (
                  <tr key={key + ':' + idx} className="hover:bg-slate-50" data-testid={`rbac-tuning-row-${item.role}-${item.resource}`}>
                    <td className="px-4 py-3 font-medium text-slate-900">{item.role}</td>
                    <td className="px-4 py-3 text-slate-700">{item.resource}</td>
                    <td className="px-4 py-3">
                      <Badge className={LEVEL_COLOR[item.required] || 'bg-slate-100 text-slate-700'}>
                        {item.required}
                      </Badge>
                    </td>
                    <td className="px-4 py-3 text-right font-mono text-slate-900">{item.count}</td>
                    <td className="px-4 py-3 text-slate-600">
                      <div className="flex items-center gap-1 text-xs">
                        <UsersIcon className="w-3 h-3" />
                        {item.user_count} × — {item.users.slice(0, 3).join(', ')}
                        {item.user_count > 3 && ` +${item.user_count - 3}`}
                      </div>
                      {item.sample_paths.length > 0 && (
                        <div className="text-xs text-slate-400 mt-1 font-mono truncate max-w-md" title={item.sample_paths.join(' | ')}>
                          {item.sample_paths[0]}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3 text-xs text-slate-500">
                      <div className="flex items-center gap-1">
                        <Clock className="w-3 h-3" /> {formatRelative(item.last_ts)}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <Button
                        size="sm"
                        disabled={!roleExists || isApplying}
                        onClick={() => applyPermission(item)}
                        className="bg-brand-red hover:bg-brand-red/90 text-white"
                        data-testid={`rbac-tuning-apply-${item.role}-${item.resource}`}
                        title={roleExists
                          ? `Rolle "${item.role}" um ${item.resource}:${item.required} erweitern`
                          : 'Rolle nicht gefunden — in Rollen-Tab manuell anlegen'}
                      >
                        {isApplying
                          ? <><Loader2 className="w-3 h-3 mr-1 animate-spin" /> …</>
                          : <><Plus className="w-3 h-3 mr-1" /> Recht hinzufügen</>}
                      </Button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function KpiCard({ label, value }) {
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-4">
      <p className="text-xs uppercase tracking-wide text-slate-500">{label}</p>
      <p className="text-2xl font-bold text-slate-900 mt-1">{value}</p>
    </div>
  );
}
